/**
 * PwaInstallNudge — subtle "Install Cyannect to your home screen" banner on
 * the shopper Home. Behaviour:
 *   · Chrome / Edge / Android: uses the `beforeinstallprompt` event.
 *   · iOS Safari: shows a modal with visual "Share → Add to Home Screen"
 *     instructions (Apple blocks programmatic install).
 *   · Silently no-op when already installed, when the shopper dismissed the
 *     nudge in the last 30 days, or when we're on desktop with no install
 *     event fired within 3 seconds.
 */
import { useEffect, useRef, useState } from "react";
import { X, Smartphone, Share, Plus } from "lucide-react";
import { logAdConversion } from "@/lib/marketingAttribution";

const DISMISS_KEY = "cyannect.pwa_nudge.dismissed_at";
const DISMISS_TTL_DAYS = 30;

const isIOSSafari = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const webkit = /WebKit/.test(ua);
  const notChrome = !/CriOS|FxiOS|EdgiOS/.test(ua);
  return iOS && webkit && notChrome;
};

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const wasDismissedRecently = () => {
  try {
    const at = localStorage.getItem(DISMISS_KEY);
    if (!at) return false;
    const age = (Date.now() - Number(at)) / 86400000;
    return age < DISMISS_TTL_DAYS;
  } catch { return false; }
};

export default function PwaInstallNudge() {
  const deferredPrompt = useRef(null);
  const [visible, setVisible] = useState(false);
  const [showIosSheet, setShowIosSheet] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isStandalone() || wasDismissedRecently()) return;

    const onPrompt = (e) => {
      e.preventDefault();
      deferredPrompt.current = e;
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS Safari never fires beforeinstallprompt — show our manual sheet
    // after a short delay so it doesn't compete with the first paint.
    let iosTimer;
    if (isIOSSafari()) {
      iosTimer = setTimeout(() => setVisible(true), 4000);
    }

    const onInstalled = () => {
      setVisible(false);
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
      // Marketing attribution — fire the pwa_install conversion event so we
      // can attribute this install back to whichever ad brought the shopper.
      logAdConversion("pwa_install");
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setVisible(false);
    setShowIosSheet(false);
  };

  const install = async () => {
    if (isIOSSafari()) {
      setShowIosSheet(true);
      return;
    }
    const p = deferredPrompt.current;
    if (!p) return dismiss();
    p.prompt();
    try {
      await p.userChoice;
    } catch {}
    deferredPrompt.current = null;
    dismiss();
  };

  if (!visible) return null;

  return (
    <>
      <div className="fixed bottom-4 inset-x-4 md:inset-x-auto md:right-6 md:bottom-6 z-40 max-w-sm md:ml-auto"
           data-testid="pwa-install-nudge">
        <div className="pf-card p-4 flex items-start gap-3 bg-gradient-to-br from-cyan-50 to-emerald-50 border-cyan-200/60 shadow-2xl shadow-cyan-500/10">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-emerald-500 text-white flex items-center justify-center shrink-0">
            <Smartphone className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-900 text-sm">Install Cyannect to your home screen</div>
            <div className="text-xs text-slate-600 mt-0.5">One-tap access to your favourite brands, coupons and cashback — no store download needed.</div>
            <div className="flex items-center gap-2 mt-3">
              <button onClick={install}
                      className="pf-btn-primary px-4 py-1.5 rounded-full text-xs font-semibold"
                      data-testid="pwa-install-yes">
                {isIOSSafari() ? "Show me how" : "Install"}
              </button>
              <button onClick={dismiss}
                      className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold"
                      data-testid="pwa-install-later">
                Not now
              </button>
            </div>
          </div>
          <button onClick={dismiss}
                  className="text-slate-400 hover:text-slate-700 -m-1 p-1"
                  aria-label="Dismiss"
                  data-testid="pwa-install-close">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* iOS Safari instructions sheet */}
      {showIosSheet && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-end md:items-center justify-center p-4"
             onClick={dismiss} data-testid="pwa-ios-sheet">
          <div onClick={(e) => e.stopPropagation()}
               className="pf-card p-6 max-w-md w-full space-y-4 relative">
            <button onClick={dismiss}
                    className="absolute top-3 right-3 text-slate-400 hover:text-slate-700"
                    aria-label="Close"
                    data-testid="pwa-ios-close">
              <X className="w-4 h-4" />
            </button>
            <div className="font-display text-slate-900 text-xl">Install Cyannect on your iPhone</div>
            <p className="text-sm text-slate-600">Two taps and you'll get an app icon on your home screen — you'll be able to open Cyannect like any other app and get full push notifications.</p>
            <ol className="space-y-3">
              <li className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                <div className="w-7 h-7 rounded-full bg-cyan-500 text-white text-xs font-semibold flex items-center justify-center shrink-0">1</div>
                <div className="text-sm text-slate-700 flex-1">
                  Tap the <b className="inline-flex items-center gap-1 mx-1"><Share className="w-4 h-4 text-cyan-600" /> Share</b> icon at the bottom of the Safari screen.
                </div>
              </li>
              <li className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                <div className="w-7 h-7 rounded-full bg-cyan-500 text-white text-xs font-semibold flex items-center justify-center shrink-0">2</div>
                <div className="text-sm text-slate-700 flex-1">
                  Scroll and tap <b className="inline-flex items-center gap-1 mx-1"><Plus className="w-4 h-4 text-cyan-600" /> Add to Home Screen</b>, then tap <b>Add</b>.
                </div>
              </li>
            </ol>
            <button onClick={dismiss}
                    className="pf-btn-primary w-full py-2.5 rounded-full text-sm font-semibold"
                    data-testid="pwa-ios-done">
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}
